import { useEffect } from "react";
import "./ScrollHints.css";

const landscapeQuery =
    "(max-width: 1023px) and (max-height: 500px) and (orientation: landscape) and (hover: none) and (pointer: coarse)";

// One shared observer covers page scrolling and independently scrolling dialogs.
export function ScrollHints(): null {
    useEffect(() => {
        const media = window.matchMedia(landscapeQuery);
        let stop: (() => void) | undefined;

        const start = (): (() => void) => {
            const hints = new Map<HTMLElement, HTMLElement>();
            let frame = 0;
            let rescan = true;
            const root = document.scrollingElement as HTMLElement | null;
            const viewport = window.visualViewport;

            const release = (element: HTMLElement, hint: HTMLElement): void => {
                hint.remove();
                hints.delete(element);
                resize.unobserve(element);
            };

            const update = (): void => {
                frame = 0;
                mutations.disconnect();
                if (rescan) {
                    rescan = false;
                    const elements = Array.from(document.querySelectorAll<HTMLElement>("body, body *"));
                    if (root) elements.push(root);
                    for (const element of elements) {
                        if (hints.has(element) || element.clientHeight === 0) continue;
                        const style = getComputedStyle(element);
                        const scrollable = element === root || /^(auto|scroll)$/.test(style.overflowY);
                        if (!scrollable || element.scrollHeight <= element.clientHeight + 2) continue;
                        const hint = document.createElement("div");
                        hint.className = "mobile-scroll-hint";
                        hint.setAttribute("aria-hidden", "true");
                        hint.innerHTML =
                            "<svg viewBox='0 0 24 24' width='20' height='24' focusable='false'><path d='m5 5 7 6 7-6M5 12l7 6 7-6' fill='none' stroke='currentColor' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/></svg>";
                        document.body.appendChild(hint);
                        hints.set(element, hint);
                        resize.observe(element);
                    }
                }

                const left = viewport?.offsetLeft ?? 0;
                const top = viewport?.offsetTop ?? 0;
                const width = viewport?.width ?? window.innerWidth;
                const height = viewport?.height ?? window.innerHeight;
                hints.forEach((hint, element) => {
                    if (!element.isConnected || element.scrollHeight <= element.clientHeight + 2) {
                        release(element, hint);
                        return;
                    }
                    const bounds = element.getBoundingClientRect();
                    const right =
                        element === root
                            ? Math.min(left + width, element.clientWidth)
                            : Math.min(bounds.left + element.clientLeft + element.clientWidth, left + width);
                    const bottom =
                        element === root
                            ? top + height
                            : Math.min(bounds.top + element.clientTop + element.clientHeight, top + height);
                    const visibleTop = element === root ? top : Math.max(bounds.top, top);
                    const visibleLeft = element === root ? left : Math.max(bounds.left, left);
                    // Hide hints for the background behind a modal or a clipped scroll region.
                    const front = document.elementFromPoint((visibleLeft + right) / 2, bottom - 16);
                    const modal = front?.closest("[data-scroll-hint-modal], [role=dialog], .modal.is-active");
                    const unobstructed =
                        front !== null &&
                        (front === element || element.contains(front)) &&
                        (!modal || modal.contains(element));
                    const remaining = element.scrollHeight - element.clientHeight - element.scrollTop > 2;
                    hint.hidden = !remaining || !unobstructed || bottom - visibleTop < 32 || right - visibleLeft < 32;
                    hint.style.left = `${right - 26}px`;
                    hint.style.top = `${bottom - 30}px`;
                });
                mutations.observe(document.body, {
                    childList: true,
                    subtree: true,
                    characterData: true,
                    attributes: true,
                    attributeFilter: ["class", "style", "hidden", "open"]
                });
            };
            const schedule = (): void => {
                if (!frame) frame = requestAnimationFrame(update);
            };
            const refresh = (): void => {
                rescan = true;
                schedule();
            };
            const resize = new ResizeObserver(refresh);
            resize.observe(document.body);
            if (root) resize.observe(root);
            const mutations = new MutationObserver((records) => {
                if (
                    records.some(
                        (record) => !(record.target instanceof Element) || !record.target.closest(".mobile-scroll-hint")
                    )
                ) {
                    refresh();
                }
            });
            document.addEventListener("scroll", schedule, true);
            document.addEventListener("load", refresh, true);
            window.addEventListener("resize", refresh);
            viewport?.addEventListener("resize", refresh);
            viewport?.addEventListener("scroll", schedule);
            refresh();

            return () => {
                cancelAnimationFrame(frame);
                mutations.disconnect();
                resize.disconnect();
                document.removeEventListener("scroll", schedule, true);
                document.removeEventListener("load", refresh, true);
                window.removeEventListener("resize", refresh);
                viewport?.removeEventListener("resize", refresh);
                viewport?.removeEventListener("scroll", schedule);
                hints.forEach((hint, element) => release(element, hint));
            };
        };
        const sync = (): void => {
            stop?.();
            stop = media.matches ? start() : undefined;
        };
        sync();
        media.addEventListener("change", sync);
        return () => {
            media.removeEventListener("change", sync);
            stop?.();
        };
    }, []);
    return null;
}
